// One traveller's face, from one traveller record.
//
// Every avatar in the product goes through here rather than through the ui
// package's <Avatar> directly, because a traveller carries four things the
// drawing needs (the seed, the name, the initials fallback and an uploaded
// photo) and repeating that incantation at a dozen call sites is how one of
// them ends up missing. `photoUrl` is the one that matters: it arrived on
// PublicUser in round 5, and a call site that forgot it would keep drawing
// the generated character for a traveller who has uploaded a real photo.
import { Avatar, type AvatarSize } from "@traveldiary/ui";
import { avatarSeedOf } from "../lib/avatar";
import type { PublicUser } from "../lib/types";

export interface TravellerAvatarProps {
  /** Null renders the initials fallback rather than nothing. */
  user: Pick<PublicUser, "id" | "displayName" | "initials"> &
    Partial<Pick<PublicUser, "avatarSeed" | "photoUrl">> | null | undefined;
  size?: AvatarSize;
  /** The signed in traveller, marked by a heavier outline, never by colour. */
  isSelf?: boolean;
  /** Omit inside a row that already names the person. */
  label?: string | null;
  /** Name to show when the record itself is missing, e.g. a local draft. */
  fallbackName?: string;
}

export function TravellerAvatar({
  user,
  size,
  isSelf,
  label,
  fallbackName = "",
}: TravellerAvatarProps) {
  return (
    <Avatar
      seed={avatarSeedOf(user)}
      displayName={user?.displayName ?? fallbackName}
      initials={user?.initials ?? ""}
      photoUrl={user?.photoUrl ?? null}
      size={size}
      isSelf={isSelf}
      label={label}
    />
  );
}
